import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, CheckCircle, ShieldCheck, GraduationCap, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

export default function ActivateAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const isParent = searchParams.get('type') === 'parent';

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Parent-specific state
  const [step, setStep] = useState('activate'); // activate | review | consent
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [activated, setActivated] = useState(false);

  // Validate parent token on load
  useEffect(() => {
    if (token && isParent) {
      setTokenLoading(true);
      api.get(`/auth/validate-parent-token?token=${token}`)
        .then(r => {
          setTokenInfo(r.data);
          if (r.data.already_active) {
            setStep('review');
            setActivated(true);
          }
        })
        .catch(e => setTokenError(e.response?.data?.detail || 'Invalid or expired invitation link'))
        .finally(() => setTokenLoading(false));
    }
  }, [token, isParent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const endpoint = isParent ? '/auth/activate-parent' : '/auth/activate-account';
      const r = await api.post(endpoint, { token, password });
      setActivated(true);
      if (isParent) {
        setStep('review');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Activation failed. The link may have expired.');
    }
    setLoading(false);
  };

  // No token
  if (!token) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Invalid Activation Link</h1>
          <p className="text-sm text-[#4B5563] mt-2">This activation link is invalid or has expired. Please contact your school administrator for a new link.</p>
        </div>
      </div>
    );
  }

  // Parent flow: token loading
  if (isParent && tokenLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <Loader2 className="h-8 w-8 animate-spin text-[#F97D6A]" />
      </div>
    );
  }

  // Parent flow: token error
  if (isParent && tokenError) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Invitation Link Invalid</h1>
          <p className="text-sm text-[#4B5563] mt-2">{tokenError}</p>
          <Link to="/login"><Button variant="outline" className="mt-4 rounded-xl">Go to Sign In</Button></Link>
        </div>
      </div>
    );
  }

  // ─── Parent Step: Review Students ───
  if (isParent && step === 'review' && tokenInfo) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-6 w-6 text-[#F97D6A]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Welcome to CareersPOV</h1>
              <p className="text-sm text-[#4B5563] mt-1">Your school has registered the following student(s):</p>
            </div>

            {tokenInfo.children?.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6" data-testid="student-review-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Student</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Grade</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Division</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">School</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokenInfo.children.map((c, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`review-student-${i}`}>
                        <td className="px-4 py-2.5 font-medium text-[#1F2937]">{c.name}</td>
                        <td className="px-4 py-2.5 text-[#4B5563]">{c.grade}</td>
                        <td className="px-4 py-2.5 text-[#4B5563]">{c.division || '—'}</td>
                        <td className="px-4 py-2.5 text-[#4B5563]">{c.school_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-[#9CA3AF] mb-6 text-center">
              All information is read-only. If any student information is incorrect, please contact your Career Coordinator.
            </p>

            <Button onClick={() => setStep('consent')} className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold" data-testid="review-continue-btn">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Parent Step: Consent — Redirect to actual consent page ───
  if (isParent && step === 'consent') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up text-center">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-2">Account Activated!</h1>
            <p className="text-sm text-[#4B5563] mb-2">
              Your account is now active. One more step remains — please sign in to complete privacy consent for your child.
            </p>
            <Badge className="border-0 bg-amber-50 text-amber-700 text-xs mb-6">Privacy Consent Required</Badge>
            <Link to="/login?redirect=/parent/consent">
              <Button className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold" data-testid="go-to-login-btn">
                Sign In & Complete Consent <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Standard activation (coordinator) or parent activation screen ───
  const alreadyActive = isParent && tokenInfo?.already_active;

  if (alreadyActive) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 text-blue-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Account Already Active</h1>
            <p className="text-sm text-[#4B5563] mt-2 mb-1">
              Welcome back, <strong>{tokenInfo.parent_name}</strong>! Your account is already activated.
            </p>
            {tokenInfo.children?.length > 0 && (
              <p className="text-xs text-[#6B7280] mb-4">
                New students from <strong>{tokenInfo.school_name}</strong> have been linked to your account.
              </p>
            )}
            <Link to="/login">
              <Button className="mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold w-full" data-testid="go-to-login-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Non-parent success (coordinator activation)
  if (!isParent && activated) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
            <div className="text-center" data-testid="activation-success-message">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Account Activated</h1>
              <p className="text-sm text-[#4B5563] mt-2">Your account is now active. You can sign in with your email and password.</p>
              <Link to="/login">
                <Button className="mt-6 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="activate-account-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-6 w-6 text-[#F97D6A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Activate Your Account</h1>
            {isParent && tokenInfo ? (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-[#4B5563]">{tokenInfo.school_name}</p>
                <p className="text-sm font-medium text-[#1F2937]">{tokenInfo.parent_name}</p>
                <p className="text-xs text-[#9CA3AF]">{tokenInfo.email}</p>
              </div>
            ) : (
              <p className="text-sm text-[#4B5563] mt-1">Set a password to complete your account setup.</p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6" data-testid="activation-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="activate-account-form">
            {isParent && tokenInfo && (
              <div className="bg-[#F8F9FA] rounded-xl px-4 py-3 mb-2">
                <Label className="text-xs text-[#9CA3AF] uppercase tracking-wider">Email</Label>
                <p className="text-sm font-medium text-[#1F2937] mt-0.5">{tokenInfo.email}</p>
              </div>
            )}
            <div>
              <Label htmlFor="password" className="text-[#1F2937] font-medium">Create Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password" type={showPass ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" required
                  className="rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A] pr-10"
                  data-testid="activation-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-[#1F2937] font-medium">Confirm Password</Label>
              <Input
                id="confirm" type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required
                className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                data-testid="activation-confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
              data-testid="activation-submit-btn">
              {loading ? 'Activating...' : 'Activate Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
